import {
  AccreditationStatus,
  Chapter,
  ChapterType,
  Customer,
  DocumentClassification,
  Examination,
  IssueStatus,
  Project,
  Report,
} from "graphql/types"
import { v4 } from "uuid"
// COMMENTED OUT: Appendix functions not needed since appendix is customer-provided
// import {
//   getConformityAssessmentAppendix,
//   getConnectionDescriptions,
//   getEquipmentEntranceExamination,
//   getExternalWitnessingPersons,
//   getFunctionalChecksDocumentation,
//   getMeasurementUncertainty,
//   getTestEquipmentCalibration,
//   getTestMethodDescription,
//   getTestParametersWithValues,
// } from "./buildReportAppendix"
// No longer needed since we're preserving edited chapters
// import { generateMultiTestChapters } from "./buildMultiTestChapters"
import {
  getCoverPage,
  // getOperatingModeDescriptions, // COMMENTED OUT: Not needed without appendix
  // getPassFailCriteriaDefinitions, // COMMENTED OUT: Not needed without appendix
  getRevisionHistory,
  getTableOfContents,
} from "./buildReportEnhanced"
import { getTREOHeader } from "./buildReportProfessional"

/**
 * Enhance an existing report with professional formatting
 * This function ONLY adds structural chapters (Cover Page, TOC, List of Revisions)
 * All existing report content from the editor is preserved as-is
 * The PDF should be a faithful representation of what users see in the report editor
 */
export const enhanceReportWithProfessionalFormat = (
  report: Report,
  project: Project,
  examinations: Examination[] = [],
  customer?: Customer | null
): Report => {
  // Use actual report values, only set defaults for truly optional display preferences
  const enhancedReport: Report = {
    ...report,
    examinations: examinations.length > 0 ? examinations : report.examinations, // Use passed examinations if available
    classification: report.classification || DocumentClassification.NOT_CLASSIFIED,
    issueStatus: report.issueStatus || IssueStatus.DRAFT,
    // Don't auto-set watermark - let PDFExportButton handle preview vs export logic
    showDraftWatermark: report.showDraftWatermark ?? false,
    accreditationStatus: report.accreditationStatus || AccreditationStatus.ACCREDITED,
    revisions: report.revisions || [],
    // Don't override with hardcoded values - use actual data from report
    formNumber: report.formNumber,
    formRevision: report.formRevision,
    testFacility: report.testFacility,
    reviewerTitle: report.reviewerTitle,
    // Store project and customer data for use in PDFDocument
    project: project as any,
    customer: customer as any,
  } as Report

  // Create professional cover page chapter with TREO branding
  const coverPageData = [getTREOHeader(), ...getCoverPage(enhancedReport, project)]

  const coverPageChapter: Chapter = {
    id: v4(),
    chapterNumber: "",
    name: "Cover Page",
    type: ChapterType.OVERVIEW,
    data: coverPageData,
    subChapter: [],
    open: false,
  }

  // Create List of Revisions chapter
  const revisionHistoryChapter: Chapter = {
    id: v4(),
    chapterNumber: "",
    name: "List of Revisions",
    type: ChapterType.OVERVIEW,
    data: getRevisionHistory(enhancedReport),
    subChapter: [],
    open: false,
  }

  // We'll update the TOC after organizing all chapters
  const tocChapter: Chapter = {
    id: v4(),
    chapterNumber: "",
    name: "Table of Contents",
    type: ChapterType.INDEX,
    data: [], // Will be populated after all chapters are organized
    subChapter: [],
    open: false,
  }

  // COMMENTED OUT: Appendices will be hardcoded content provided by customer
  // Create appendices if examinations are provided
  // const appendixChapters: Chapter[] = []

  // Use the first examination for appendices (or combine data from all)
  // const primaryExamination = examinations[0]

  // if (primaryExamination) {
  //   // Equipment Entrance Examination
  // const entranceExamChapter: Chapter = {
  //   id: v4(),
  //   chapterNumber: "A.1",
  //   name: "Equipment Entrance Examination",
  //   type: ChapterType.APPENDIX,
  //   data: getEquipmentEntranceExamination(primaryExamination.deviceSetup?.map((ds) => ds.device)),
  //   subChapter: [],
  //   open: false,
  // }
  // appendixChapters.push(entranceExamChapter)

  // Test Method Description
  // const testMethodChapter: Chapter = {
  //   id: v4(),
  //   chapterNumber: "A.2",
  //   name: "Test Method Description",
  //   type: ChapterType.APPENDIX,
  //   data: getTestMethodDescription(primaryExamination),
  //   subChapter: [],
  //   open: false,
  // }
  // appendixChapters.push(testMethodChapter)

  // Test Parameters with Values
  // const testParamsChapter: Chapter = {
  //   id: v4(),
  //   chapterNumber: "A.3",
  //   name: "Test Parameters",
  //   type: ChapterType.APPENDIX,
  //   data: getTestParametersWithValues(primaryExamination),
  //   subChapter: [],
  //   open: false,
  // }
  // appendixChapters.push(testParamsChapter)

  // Pass/Fail Criteria Definitions
  // const passCriteriaChapter: Chapter = {
  //   id: v4(),
  //   chapterNumber: "A.4",
  //   name: "Pass/Fail Criteria Definitions",
  //   type: ChapterType.APPENDIX,
  //   data: getPassFailCriteriaDefinitions(),
  //   subChapter: [],
  //   open: false,
  // }
  // appendixChapters.push(passCriteriaChapter)

  // Operating Mode Descriptions
  // const operatingModesChapter: Chapter = {
  //   id: v4(),
  //   chapterNumber: "A.5",
  //   name: "Operating Mode Descriptions",
  //   type: ChapterType.APPENDIX,
  //   data: getOperatingModeDescriptions(primaryExamination),
  //   subChapter: [],
  //   open: false,
  // }
  // appendixChapters.push(operatingModesChapter)

  // Functional Checks Documentation
  // const functionalChecksChapter: Chapter = {
  //   id: v4(),
  //   chapterNumber: "A.6",
  //   name: "Functional Checks Documentation",
  //   type: ChapterType.APPENDIX,
  //   data: getFunctionalChecksDocumentation(),
  //   subChapter: [],
  //   open: false,
  // }
  // appendixChapters.push(functionalChecksChapter)

  // Connection Descriptions
  // const connectionDescChapter: Chapter = {
  //   id: v4(),
  //   chapterNumber: "A.7",
  //   name: "Connection Descriptions",
  //   type: ChapterType.APPENDIX,
  //   data: getConnectionDescriptions(),
  //   subChapter: [],
  //   open: false,
  // }
  // appendixChapters.push(connectionDescChapter)

  // Measurement Uncertainty
  // const measurementUncertaintyChapter: Chapter = {
  //   id: v4(),
  //   chapterNumber: "A.8",
  //   name: "Measurement Uncertainty",
  //   type: ChapterType.APPENDIX,
  //   data: getMeasurementUncertainty(
  //     primaryExamination.deviceSetup
  //       ?.filter((ds) => ds.device?.type === "TEST_EQUIPMENT")
  //       .map((ds) => ds.device)
  //   ),
  //   subChapter: [],
  //   open: false,
  // }
  // appendixChapters.push(measurementUncertaintyChapter)

  // Test Equipment Calibration
  // const calibrationChapter: Chapter = {
  //   id: v4(),
  //   chapterNumber: "A.9",
  //   name: "Test Equipment Calibration",
  //   type: ChapterType.APPENDIX,
  //   data: getTestEquipmentCalibration(
  //     primaryExamination.deviceSetup
  //       ?.filter((ds) => ds.device?.type === "TEST_EQUIPMENT")
  //       .map((ds) => ds.device)
  //   ),
  //   subChapter: [],
  //   open: false,
  // }
  // appendixChapters.push(calibrationChapter)

  // External Witnessing Persons
  // Extract witnesses from test data within tasks
  // const allWitnesses = primaryExamination.tasks.flatMap((task) =>
  //   task.testDatas.flatMap((data) => data.witnesses || [])
  // )

  // Deduplicate and format witnesses
  // const uniqueWitnesses = allWitnesses.filter((witness, index, self) =>
  //   index === self.findIndex((w) =>
  //     w?.firstName === witness?.firstName &&
  //     w?.lastName === witness?.lastName
  //   )
  // )

  // const witnesses = uniqueWitnesses.map(w => ({
  //   name: `${w?.firstName || ""} ${w?.lastName || ""}`.trim() || "Unknown",
  //   company: "NEED REAL DATA HERE - Person type missing company field"
  // }))

  // const witnessChapter: Chapter = {
  //   id: v4(),
  //   chapterNumber: "A.10",
  //   name: "External Witnessing Persons",
  //   type: ChapterType.APPENDIX,
  //   data: getExternalWitnessingPersons(witnesses),
  //   subChapter: [],
  //   open: false,
  // }
  // appendixChapters.push(witnessChapter)
  // }

  // Conformity Assessment Appendix (always included)
  // const conformityAppendixChapter: Chapter = {
  //   id: v4(),
  //   chapterNumber: "B",
  //   name: "Conformity Assessment Rule",
  //   type: ChapterType.APPENDIX,
  //   data: getConformityAssessmentAppendix(),
  //   subChapter: [],
  //   open: false,
  // }
  // appendixChapters.push(conformityAppendixChapter)

  // Create main appendix chapter with sub-chapters
  // const appendixMainChapter: Chapter = {
  //   id: v4(),
  //   chapterNumber: "Appendix",
  //   name: "Appendices",
  //   type: ChapterType.APPENDIX,
  //   data: [],
  //   subChapter: appendixChapters,
  //   open: false,
  // }

  // IMPORTANT: Preserve ALL existing chapters from the edited report
  // The report editor is where users craft their content - we should use it as-is
  const existingChapters = enhancedReport.chapter

  // Simply add structural chapters at the beginning, keep everything else
  const reorganizedChapters = [
    coverPageChapter,
    revisionHistoryChapter,
    tocChapter,
    ...existingChapters, // Keep ALL edited chapters exactly as they are
  ]

  // Now update the TOC with the actual chapters
  tocChapter.data = getTableOfContents(enhancedReport, reorganizedChapters)

  return {
    ...enhancedReport,
    chapter: reorganizedChapters,
  }
}

// NOTE: Helper functions for auto-generating content from examination data have been removed
// See REMOVED_PDF_AUTOGENERATION_FEATURES.md for documentation of removed features
// The PDF now preserves all edited report content instead of auto-generating

// The following functions were removed: createEquipmentUnderTestData, createAuxiliaryEquipmentData,
// createTestSummaryData, getTestPeriod. They auto-generated content from examination/project data.
// Now the PDF uses the edited report content directly.
/**
 * Check if a report should show the draft watermark
 */
export const shouldShowDraftWatermark = (report: Report): boolean => {
  // Show watermark if explicitly set to true OR if status is DRAFT and not explicitly set to false
  if (report.showDraftWatermark === true) return true
  if (report.showDraftWatermark === false) return false
  return report.issueStatus === IssueStatus.DRAFT
}

/**
 * Get classification label for display
 */
export const getClassificationLabel = (classification?: DocumentClassification): string => {
  switch (classification) {
    case DocumentClassification.CLASSIFIED:
      return "Classified"
    case DocumentClassification.CONFIDENTIAL:
      return "Confidential"
    case DocumentClassification.SECRET:
      return "Secret"
    case DocumentClassification.NOT_CLASSIFIED:
    default:
      return "Not Classified"
  }
}

/**
 * Get issue status label for display
 */
export const getIssueStatusLabel = (status?: IssueStatus): string => {
  switch (status) {
    case IssueStatus.DRAFT:
      return "DRAFT"
    case IssueStatus.REVIEW:
      return "REVIEW"
    case IssueStatus.APPROVED:
      return "APPROVED"
    case IssueStatus.FINAL:
      return "FINAL"
    case IssueStatus.ARCHIVED:
      return "ARCHIVED"
    default:
      return "DRAFT"
  }
}

/**
 * Get accreditation status symbol
 */
export const getAccreditationSymbol = (status?: AccreditationStatus): string => {
  switch (status) {
    case AccreditationStatus.NON_ACCREDITED:
      return "*"
    case AccreditationStatus.EXTERNAL_ACCREDITED:
      return "**"
    case AccreditationStatus.EXTERNAL_NON_ACCREDITED:
      return "***"
    case AccreditationStatus.ACCREDITED:
    default:
      return ""
  }
}
